import React from 'react';
import {graphql} from 'react-relay';
import {Redirect} from 'react-router-dom';
import Styled from 'styled-components/macro';
import {ForecastQueryRenderer} from '../../ForecastQueryRenderer';
import CompanyViewHeader from './CompanyViewHeader';
import CompanyViewOverview from './CompanyViewOverview';

// The 1920px layout fills the content column fluidly. On ultra-wide screens it would otherwise
// sprawl edge-to-edge, so we cap + center the page content at >=2137px — there is no dedicated
// 2560px design, the 1920px layout just has to scale gracefully (IGNITE-479 AC). Mirrors the
// my-timesheets-page convention (2137px breakpoint).
const PageContent = Styled.div`
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;
	min-height: 0;
	width: 100%;
	@media screen and (min-width: 2137px) {
		max-width: 2089px;
		margin: 0 auto;
	}
`;

type ClientStatus = {
	id: string;
	standing: 'ACTIVE' | 'INACTIVE';
};

type PrimaryContact = {
	id: string;
	firstName: string | null;
	lastName: string | null;
};

type ProjectNode = {
	id: string;
	name: string | null;
	companyProjectId: number | null;
	fullAccessToProject: boolean | null;
	projectEndYear: number | null;
	projectEndMonth: number | null;
	projectEndDay: number | null;
};

export type ClientContactNode = {
	id: string;
	firstName: string | null;
	lastName: string | null;
	email: string | null;
	phone: string | null;
	jobTitle: string | null;
	status: string | null;
	isGuestUser: boolean | null;
	createdAt: string | null;
	updatedAt: string | null;
	projects: {
		edges: Array<{node: ProjectNode | null} | null> | null;
	} | null;
};

type Client = {
	id: string;
	name: string;
	website: string | null;
	city: string | null;
	country: string | null;
	region: string | null;
	statusId: string | null;
	createdAt: string | null;
	updatedAt: string | null;
	notes: string | null;
	notesUpdatedAt: string | null;
};

type InnerProps = {
	client: Client | null;
	status: ClientStatus | null;
	primaryContact: PrimaryContact | null;
	clientContacts: ClientContactNode[];
	companyId: string;
};

export const CompanyViewPageInner: React.FC<InnerProps> = ({client, status, primaryContact, clientContacts, companyId}) => {
	if (!client) {
		// Invalid / non-existent client ID behind the feature flag → 404 (IGNITE-479 AC).
		return <Redirect to="/not-found" />;
	}
	return (
		<PageContent>
			<CompanyViewHeader
				client={client}
				status={status}
				primaryContact={primaryContact}
				clientContacts={clientContacts}
				companyId={companyId}
			/>
			<CompanyViewOverview clientId={client.id} notes={client.notes} notesUpdatedAt={client.notesUpdatedAt} />
		</PageContent>
	);
};

const companyViewPageQuery = graphql`
	query CompanyViewPage_Query($clientId: String) {
		viewer {
			actualPersonId
			company {
				id
				client(id: $clientId) {
					id
					name
					website
					city
					country
					region
					statusId
					createdAt
					updatedAt
					notes
					notesUpdatedAt
					status {
						id
						standing
					}
					primaryContact {
						id
						firstName
						lastName
					}
					clientContacts(first: 1000) {
						edges {
							node {
								id
								firstName
								lastName
								email
								phone
								jobTitle
								status
								isGuestUser
								createdAt
								updatedAt
								projects(first: 100) {
									edges {
										node {
											id
											name
											companyProjectId
											fullAccessToProject
											projectEndYear
											projectEndMonth
											projectEndDay
										}
									}
								}
							}
						}
					}
				}
			}
		}
	}
`;

type CompanyViewPageProps = {
	clientId: string;
};

const CompanyViewPage: React.FC<CompanyViewPageProps> = ({clientId}) => {
	return (
		<ForecastQueryRenderer
			key={'query-render-companyViewPage-' + clientId}
			query={companyViewPageQuery}
			variables={{clientId}}
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			render={(relayProps: any) => {
				const company = relayProps?.viewer?.company;
				const client = company?.client ?? null;
				const status = client?.status ?? null;
				const primaryContact = client?.primaryContact ?? null;
				const clientContacts: ClientContactNode[] =
					client?.clientContacts?.edges?.flatMap((e: {node: ClientContactNode | null} | null) =>
						e?.node ? [e.node] : []
					) ?? [];
				const companyId = company?.id ?? '';
				return (
					<CompanyViewPageInner
						client={client}
						status={status}
						primaryContact={primaryContact}
						clientContacts={clientContacts}
						companyId={companyId}
					/>
				);
			}}
		/>
	);
};

export default CompanyViewPage;
